import React from 'react';
import { Link } from 'react-router-dom';
import Viewer360 from '../components/viewer360/Viewer360';
import FancyButton from '../components/FancyButton';

const Home = () => {
  const [formData, setFormData] = React.useState({ name: '', email: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      setFormStatus(data.success ? 'success' : 'error');
    } catch {
      setFormStatus('error');
    }
  };
  const heroSlides = [
    { label: 'Studio Fernandes', src: '/images/home-banner.webp' },
    { label: 'Showcase 1', src: '/images/home-slide-gemini-n66j8d.webp' },
    { label: 'Showcase 2', src: '/images/home-slide-unnamed-37.webp' },
    { label: 'Showcase 3', src: '/images/home-slide-gemini-bhk6ow.webp' },
    { label: 'Showcase 4', src: '/images/home-slide-unnamed-38.webp' },
    { label: 'Showcase 5', src: '/images/home-slide-womenswear.webp' },
  ];
  const [activeHeroSlide, setActiveHeroSlide] = React.useState(0);

  React.useEffect(() => {
    if (heroSlides.length < 2) return undefined;
    const slideInterval = setInterval(() => {
      setActiveHeroSlide((currentIndex) => (currentIndex + 1) % heroSlides.length);
    }, 4500);
    return () => clearInterval(slideInterval);
  }, [heroSlides.length]);

  return (
    <>
      {/* Hero Section */}
      {/* Mobile banner image — 16:9, full width, above text */}
      <div className="block lg:hidden relative w-full aspect-video overflow-hidden">
        {heroSlides.map((slide, index) => (
          <img key={`mob-${slide.label}-${index}`} src={slide.src} alt={slide.label} className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${index === activeHeroSlide ? 'opacity-100' : 'opacity-0'}`} />
        ))}
        <div className="absolute bottom-4 right-4 z-20 flex gap-2">
          {heroSlides.map((_, index) => (
            <button key={`mob-dot-${index}`} type="button" onClick={() => setActiveHeroSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${index === activeHeroSlide ? 'w-6 bg-white' : 'w-2 bg-white/60'}`}
              aria-label={`Show slide ${index + 1}`} />
          ))}
        </div>
      </div>
      {/* Mobile text below banner */}
      <div className="block lg:hidden px-6 py-6 bg-background-light dark:bg-background-dark">
        <span className="text-[10px] uppercase tracking-[0.5em] text-primary mb-4 block">Bespoke Tailoring &amp; Design</span>
        <h1 className="font-display text-4xl leading-[0.95] mb-4 dark:text-white">Crafting<br />Legacies<br />Since 1905</h1>
        <div className="w-10 h-px bg-primary" />
      </div>

      <header className="hidden lg:flex relative w-full lg:block lg:h-[85vh] overflow-hidden bg-background-light dark:bg-background-dark">
        {/* Left — text */}
        <div className="relative z-10 flex flex-col justify-center w-full lg:w-[28%] lg:h-full px-6 sm:px-8 md:px-12 lg:px-16 py-8 lg:py-0 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-[0.5em] text-primary mb-6">Bespoke Tailoring &amp; Design</span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6 dark:text-white">
            Crafting<br />Legacies<br />Since 1905
          </h1>
          <div className="w-10 h-px bg-primary" />
        </div>
        {/* Right — image with left blend */}
        <div className="relative w-full h-[58vh] lg:absolute lg:inset-y-0 lg:right-0 lg:w-[80%] lg:h-full order-first lg:order-none flex-shrink-0">
          {heroSlides.map((slide, index) => (
            <picture key={`${slide.label}-${index}`} className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${index === activeHeroSlide ? 'opacity-100' : 'opacity-0'}`}>
              <img
                src={slide.src}
                alt={slide.label}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </picture>
          ))}
          <div className="hidden lg:block absolute inset-y-0 left-0 w-[15%] bg-gradient-to-r from-background-light dark:from-background-dark to-transparent" />
          <div className="absolute bottom-6 right-6 z-20 flex gap-2">
            {heroSlides.map((slide, index) => (
              <button
                key={`${slide.label}-dot-${index}`}
                type="button"
                onClick={() => setActiveHeroSlide(index)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  index === activeHeroSlide ? 'w-8 bg-white' : 'w-2.5 bg-white/60 hover:bg-white/80'
                }`}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Collection Grid — "Curated Elegance" */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-12 border-b border-gray-300 dark:border-gray-700 pb-4">
          <div>
            <h2 className="text-3xl md:text-4xl font-display mb-2">Curated Elegance</h2>
            <p className="text-gray-500 dark:text-gray-400 font-light italic">Timeless pieces for the modern connoisseur</p>
          </div>
        </div>
        <div className="relative md:static">
          {/* Scroll arrows — mobile only */}
          <button
            className="md:hidden absolute left-0 top-[35%] -translate-y-1/2 z-10 bg-white/80 dark:bg-surface-dark/80 shadow rounded-full w-8 h-8 flex items-center justify-center"
            onClick={() => document.getElementById('elegance-scroll').scrollBy({ left: -280, behavior: 'smooth' })}
            aria-label="Scroll left"
          >
            <span className="material-icons text-base">chevron_left</span>
          </button>
          <button
            className="md:hidden absolute right-0 top-[35%] -translate-y-1/2 z-10 bg-white/80 dark:bg-surface-dark/80 shadow rounded-full w-8 h-8 flex items-center justify-center"
            onClick={() => document.getElementById('elegance-scroll').scrollBy({ left: 280, behavior: 'smooth' })}
            aria-label="Scroll right"
          >
            <span className="material-icons text-base">chevron_right</span>
          </button>
        <div id="elegance-scroll" className="flex gap-4 overflow-x-auto pb-2 md:pb-0 snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-5 md:gap-6 md:overflow-visible scrollbar-hide">
          {[
            { label: "Bespoke Suiting", desc: 'Crafted to your exact measurements.', image: '/images/suit-unnamed-17.webp', to: '/bespoke-suiting' },
            { label: "Wedding Tailoring", desc: 'Sophisticated cuts for your special day.', image: '/images/wedding-card.webp', to: '/weddingtailoring' },
          ].map((item) => (
            <Link key={item.label} to={item.to} className="group cursor-pointer block flex-shrink-0 w-[70vw] md:w-auto snap-start">
              <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-4 shadow-sm bg-white dark:bg-surface-dark">
                <img src={item.image} alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="card-hover-overlay">
                  <span className="text-xs uppercase tracking-wide font-medium">Explore</span>
                  <span className="material-icons text-sm">arrow_forward</span>
                </div>
              </div>
              <h3 className="font-display text-lg mb-1">{item.label}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
            </Link>
          ))}
          <div className="flex-shrink-0 w-[70vw] md:w-auto snap-start">
            <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-4 shadow-sm bg-white dark:bg-surface-dark">
              <img src="/images/bosco-bollywood.webp" alt="Bosco Bollywood" className="w-full h-full object-cover" />
            </div>
            <h3 className="font-display text-lg mb-1">Tailored for Icons, Worn by the Finest</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Worn by Bosco—crafted to perfection at our store, where celebrity style meets uncompromising tailoring at SF</p>
          </div>
          {[
            { label: 'Corporate & Uniform', desc: 'Sharp tailoring for the boardroom.', image: '/images/corporate-card.webp', to: '/corporate' },
            { label: 'Accessories', desc: 'The finishing touches.', image: '/images/accessories.webp', to: '/accessories' },
          ].map((item) => (
            <Link key={item.label} to={item.to} className="group cursor-pointer block flex-shrink-0 w-[70vw] md:w-auto snap-start">
              <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-4 shadow-sm bg-white dark:bg-surface-dark">
                <img src={item.image} alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="card-hover-overlay">
                  <span className="text-xs uppercase tracking-wide font-medium">Explore</span>
                  <span className="material-icons text-sm">arrow_forward</span>
                </div>
              </div>
              <h3 className="font-display text-lg mb-1">{item.label}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* Brand Philosophy */}
      <section className="bg-surface-light dark:bg-surface-dark py-20 md:py-32 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="w-3/4 mx-auto rounded-lg overflow-hidden shadow-sm bg-white dark:bg-surface-dark">
            <Viewer360 frames={[]} frameCount={144} />
          </div>
          <div className="text-center lg:text-left">
            <h2 className="text-3xl md:text-5xl font-display leading-tight mb-8">
              Where fabric transforms into poetry, and garments become quiet declarations of self.
              <span className="text-gray-400 dark:text-gray-600 block mt-4">Behind the velvet-draped walls of our atelier, creation is a slow and sacred ritual.</span>
            </h2>
            <div className="w-16 h-1 bg-primary mb-8 mx-auto lg:mx-0"></div>
            <p className="text-gray-600 dark:text-gray-400 font-light leading-relaxed mb-10">
              At Studio Fernandes, we believe that true style is not just about looking good—it's about the feeling of wearing something made exclusively for you. Our legacy of craftsmanship ensures every stitch tells a story.
            </p>
            <Link className="inline-block border-b border-primary text-primary hover:text-orange-700 pb-1 uppercase tracking-widest text-xs font-semibold transition-colors" to="/our-story">Read Our Story</Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-[#D8D5CD] dark:bg-[#1f1e1c] rounded-lg overflow-hidden shadow-lg p-0">
          <div className="relative h-[360px] sm:h-[420px] lg:h-[600px] w-full overflow-hidden p-4 sm:p-6 flex gap-4">
            <div className="flex-1 overflow-hidden rounded" style={{backgroundImage: 'url(/images/corporate-unnamed-13.webp)', backgroundSize: '200% 100%', backgroundPosition: '0% center'}}></div>
            <div className="flex-1 overflow-hidden rounded" style={{backgroundImage: 'url(/images/corporate-unnamed-13.webp)', backgroundSize: '200% 100%', backgroundPosition: '100% center'}}></div>
          </div>
          <div className="p-6 sm:p-8 md:p-12 lg:pr-16">
            <span className="text-xs font-bold uppercase tracking-widest text-primary mb-4 block">The Process</span>
            <h2 className="text-4xl md:text-5xl font-display mb-6 text-gray-900 dark:text-white">Tradition meets <br />Modern Precision.</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
              Born from the fusion of age-old tailoring and contemporary expression, Studio Fernandes weaves stories into fabric. Every design is an heirloom of intention, crafted with techniques passed down through generations while embracing modern silhouettes.
            </p>
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div>
                <h4 className="font-bold text-lg mb-2">Consultation</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Personal style assessment and fabric selection.</p>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2">Fitting</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Multiple fittings to ensure the perfect drape.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Look Finder */}
      <section id="contact" className="relative w-full py-12 md:py-24 px-4 bg-background-light dark:bg-background-dark">
        <div className="absolute inset-0 z-0">
          <img src="/images/store-interior-reception.webp" alt="" className="w-full h-full object-cover opacity-20 dark:opacity-40 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-light via-background-light/90 to-transparent dark:from-background-dark dark:via-background-dark/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row items-stretch gap-6">
          <div className="w-full md:w-1/3 bg-surface-light dark:bg-surface-dark p-6 sm:p-8 md:p-10 rounded-lg shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-display mb-3">Get in Touch</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm leading-relaxed">Book a consultation or ask us anything — we'll get back to you within 24 hours.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary transition-colors"
              />
              <input
                type="email"
                placeholder="Your Email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary transition-colors"
              />
              <input
                type="tel"
                placeholder="Your Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary transition-colors"
              />
              <textarea
                placeholder="Tell us about your requirements..."
                rows="4"
                required
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary transition-colors resize-none"
              />
              <div className="pt-2">
                <FancyButton label={formStatus === 'sending' ? 'Sending...' : 'Send Message'} />
              </div>
              {formStatus === 'success' && <p className="text-green-600 text-sm">Message sent successfully!</p>}
              {formStatus === 'error' && <p className="text-red-500 text-sm">Failed to send. Please try again.</p>}
            </form>
          </div>
          <div className="w-full md:w-1/3 rounded-lg shadow-2xl overflow-hidden">
            <img src="/images/get-in-touch.webp" alt="Get in Touch" className="w-full h-full object-cover" />
          </div>
          <div className="w-full md:w-1/3 rounded-lg shadow-2xl overflow-hidden">
            <video src="/images/get-in-touch-cropped.mp4" autoPlay loop muted playsInline style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
